import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const IMG = {
  // Aerial view of mining/extraction operation (evocative of lithium/salt/mining fields)
  hero: 'https://images.pexels.com/photos/2101137/pexels-photo-2101137.jpeg?auto=compress&cs=tinysrgb&w=1400',
  // Industrial energy infrastructure at sunset (petrochemical/hydrocarbons)
  transparency: 'https://images.pexels.com/photos/221012/pexels-photo-221012.jpeg?auto=compress&cs=tinysrgb&w=1200',
};

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang = 'ar' } = useParams();

  const why = t('why.items', { returnObjects: true }) || [];
  const steps = t('how.steps', { returnObjects: true }) || [];
  const sectors = t('sectors.items', { returnObjects: true }) || [];
  const bullets = t('transparency.bullets', { returnObjects: true }) || [];

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToId = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend-only form: any submission redirects to /thank_you
    navigate('/thank_you');
  };

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="hero-eyebrow">{t('hero.eyebrow')}</div>
              <h1>
                {t('hero.title').split(':')[0]}:
                {' '}
                <span className="accent">{t('hero.title').split(':')[1]}</span>
              </h1>
              <p>{t('hero.subtitle')}</p>
              <p>{t('hero.paragraph')}</p>
              <div className="hero-actions">
                <a href="#contact" onClick={scrollToContact} className="btn-primary" data-testid="hero-cta-primary">
                  {t('cta.getStarted')} <span aria-hidden>→</span>
                </a>
                <a href="#sectors" onClick={scrollToId('sectors')} className="btn-secondary" data-testid="hero-cta-secondary">
                  {t('cta.learnMore')}
                </a>
              </div>
            </div>
            <div className="hero-visual">
              <img
                src={IMG.hero}
                alt="Salares del NOA — piletas de evaporación para extracción de litio"
                loading="eager"
              />
              <div className="hero-visual-tag">
                <span className="dot" />
                Salar de Olaroz · Jujuy · 23° 30' S
              </div>
            </div>
          </div>

          <div className="hero-stats">
            <div>
              <div className="stat-value">{t('hero.stat1.value')}</div>
              <div className="stat-label">{t('hero.stat1.label')}</div>
            </div>
            <div>
              <div className="stat-value">{t('hero.stat2.value')}</div>
              <div className="stat-label">{t('hero.stat2.label')}</div>
            </div>
            <div>
              <div className="stat-value">{t('hero.stat3.value')}</div>
              <div className="stat-label">{t('hero.stat3.label')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section section-alt">
        <div className="container">
          <div className="eyebrow">{t('why.eyebrow')}</div>
          <h2 className="section-title">{t('why.title')}</h2>
          <div className="why-grid">
            {Array.isArray(why) && why.map((w, i) => (
              <article key={i} className="why-card">
                <div className="why-num">{String(i + 1).padStart(2, '0')} / 0{why.length}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section className="section" id="how">
        <div className="container">
          <div className="eyebrow">{t('how.eyebrow')}</div>
          <h2 className="section-title">{t('how.title')}</h2>
          <p className="section-intro">{t('how.intro')}</p>
          <div className="steps-grid">
            {Array.isArray(steps) && steps.map((s, i) => (
              <div key={i} className="step">
                <div className="step-num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS (dark section) */}
      <section className="section section-dark" id="sectors">
        <div className="container">
          <div className="eyebrow">{t('sectors.eyebrow')}</div>
          <h2 className="section-title">{t('sectors.title')}</h2>
          <p className="section-intro" style={{ color: 'rgba(219,228,242,0.75)' }}>{t('sectors.intro')}</p>
          <div className="sectors-list">
            {Array.isArray(sectors) && sectors.map((s, i) => (
              <a
                key={i}
                className="sector-row"
                href="#contact"
                onClick={scrollToContact}
                data-testid={`sector-row-${i}`}
              >
                <div className="sector-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="sector-tag">{s.tag}</div>
                <div>
                  <div className="sector-title-row">{s.title}</div>
                  <div className="sector-desc" style={{ marginTop: 8 }}>{s.desc}</div>
                </div>
                <div className="sector-tag">{t('cta.exploreSector')}</div>
                <div className="sector-arrow" aria-hidden>→</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="section">
        <div className="container">
          <div className="split">
            <div className="split-visual">
              <img
                src={IMG.transparency}
                alt="Infraestructura de gasoductos y transporte energético en Patagonia"
                loading="lazy"
              />
            </div>
            <div className="split-content">
              <div className="eyebrow">{t('transparency.eyebrow')}</div>
              <h2 className="section-title">{t('transparency.title')}</h2>
              <p>{t('transparency.p1')}</p>
              <p>{t('transparency.p2')}</p>
              <ul className="bullets">
                {Array.isArray(bullets) && bullets.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DASHBOARD / ANALYTICS */}
      <section className="section section-dark" id="analytics">
        <div className="container">
          <div className="eyebrow">{t('dashboard.eyebrow')}</div>
          <h2 className="section-title">{t('dashboard.title')}</h2>
          <p className="section-intro" style={{ color: 'rgba(219,228,242,0.75)' }}>{t('dashboard.desc')}</p>

          <div className="dashboard-wrap">
            <div className="dashboard-topbar">
              <div className="dots"><span/><span/><span/></div>
              <div className="file">/dashboards/vaca-muerta.report</div>
              <div style={{ width: 40 }} />
            </div>
            <div className="dashboard-body">
              <div className="chart-header">
                <div className="chart-title">
                  <h3>{t('dashboard.chartTitle')}</h3>
                  <p>{t('dashboard.chartSub')}</p>
                </div>
                <div className="chart-legend">
                  <div style={{ '--color': '#0f1f3a' }}>{t('dashboard.labels.oil')}</div>
                  <div style={{ '--color': '#c05a34' }}>{t('dashboard.labels.gas')}</div>
                  <div style={{ '--color': '#b8935a' }}>{t('dashboard.labels.lithium')}</div>
                </div>
              </div>

              <MockChart />

              <div className="metrics-row">
                <div>
                  <div className="metric-key">WTI · spot</div>
                  <div className="metric-val">USD 74.20</div>
                  <div className="metric-delta">▲ +1.8% YoY</div>
                </div>
                <div>
                  <div className="metric-key">Henry Hub</div>
                  <div className="metric-val">USD 3.42</div>
                  <div className="metric-delta down">▼ –0.6% MoM</div>
                </div>
                <div>
                  <div className="metric-key">Li₂CO₃ China</div>
                  <div className="metric-val">USD 12.9K/t</div>
                  <div className="metric-delta">▲ +4.2% QoQ</div>
                </div>
                <div>
                  <div className="metric-key">Producción VM</div>
                  <div className="metric-val">462 kboe/d</div>
                  <div className="metric-delta">▲ +11.4% YoY</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section section-alt" id="contact">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="eyebrow">{t('contact.eyebrow')}</div>
              <h2 className="section-title">{t('contact.title')}</h2>
              <p>{t('contact.desc')}</p>
              <div className="contact-meta">
                <ul>
                  <li>
                    <span className="k">Email</span>
                    <a href="mailto:contacto@argenergia.com">contacto@argenergia.com</a>
                  </li>
                  <li>
                    <span className="k">Prensa</span>
                    <a href="mailto:prensa@argenergia.com">prensa@argenergia.com</a>
                  </li>
                  <li>
                    <span className="k">Tel</span>
                    <a href="tel:+541148935200">+54 11 4893-5200</a>
                  </li>
                  <li>
                    <span className="k">Sede</span>
                    Av. Leandro N. Alem 855, Piso 14 — C1001AAD CABA
                  </li>
                </ul>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="field">
                <label htmlFor="name">{t('contact.name')}</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder={t('contact.namePh')}
                  data-testid="input-name"
                />
              </div>
              <div className="field">
                <label htmlFor="email">{t('contact.email')}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder={t('contact.emailPh')}
                  data-testid="input-email"
                />
              </div>
              <div className="field">
                <label htmlFor="message">{t('contact.message')}</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  placeholder={t('contact.messagePh')}
                  data-testid="input-message"
                />
              </div>
              <button type="submit" className="btn-primary" data-testid="submit-contact">
                {t('cta.send')} <span aria-hidden>→</span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function MockChart() {
  // A hand-crafted SVG chart with editorial look, three series across 12 months.
  const months = ['E','F','M','A','M','J','J','A','S','O','N','D'];
  const W = 1000, H = 320, P = 40;
  const chartW = W - P * 2;
  const chartH = H - P * 2;

  const oil = [310, 328, 341, 360, 372, 384, 398, 412, 428, 441, 454, 462];
  const gas = [180, 194, 210, 218, 226, 232, 244, 258, 270, 282, 291, 302];
  const li  = [ 46,  49,  52,  54,  57,  60,  62,  64,  66,  69,  71,  74];

  const scale = (arr, max) => arr.map((v, i) => [
    P + (i / (arr.length - 1)) * chartW,
    H - P - (v / max) * chartH,
  ]);
  const maxVal = 500;
  const oilPts = scale(oil, maxVal);
  const gasPts = scale(gas, maxVal);
  const liPts  = scale(li.map(v => v * 5), maxVal); // scaled visually

  const path = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');

  // grid lines
  const gridY = [0, 0.25, 0.5, 0.75, 1].map(v => H - P - v * chartH);

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="chart">
      {gridY.map((y, i) => (
        <line key={i} x1={P} x2={W - P} y1={y} y2={y} stroke="#dfe2e7" strokeDasharray="2 4" />
      ))}
      {months.map((m, i) => (
        <text
          key={m}
          x={P + (i / (months.length - 1)) * chartW}
          y={H - 14}
          fontFamily="JetBrains Mono, monospace"
          fontSize="10"
          fill="#7a7f8a"
          textAnchor="middle"
        >{m}</text>
      ))}

      {/* Area under oil */}
      <path
        d={`${path(oilPts)} L ${oilPts[oilPts.length - 1][0]} ${H - P} L ${oilPts[0][0]} ${H - P} Z`}
        fill="#0f1f3a" fillOpacity="0.06"
      />
      <path d={path(oilPts)} fill="none" stroke="#0f1f3a" strokeWidth="2.4" />
      <path d={path(gasPts)} fill="none" stroke="#c05a34" strokeWidth="2.4" />
      <path d={path(liPts)}  fill="none" stroke="#b8935a" strokeWidth="1.8" strokeDasharray="4 4" />

      {/* End dots */}
      {[[oilPts, '#0f1f3a'], [gasPts, '#c05a34'], [liPts, '#b8935a']].map(([pts, color], i) => (
        <circle key={i} cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="4" fill={color} />
      ))}
    </svg>
  );
}
